/**
 * Brand — Public brand kit page for CARTIEAI.
 *
 * Route: /brand  (no auth)
 *
 * v35.27 rewrite:
 *   - Logos: only the two approved variants (white-bg + dark-bg) — old
 *     Google-colored palette is gone.
 *   - Palette: emerald, yellow, red, maroon, white, grey, slate.
 *   - Send press / integration partners / design teams to /brand.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { Card, CardContent } from '../components/ui/card';
import {
  Download, ArrowLeft, Copy, Check, Mail, Sparkles,
} from 'lucide-react';

const LIGHT_LOGO = '/icons/cartie-logo.png';
const DARK_LOGO  = '/icons/cartie-logo-on-dark.png';

const PALETTE = [
  { name: 'Emerald',  hex: '#22A958', usage: 'Primary brand · CTA · cloud outline · "AI" mark' },
  { name: 'Yellow',   hex: '#F4C027', usage: 'Middle arrow · accent for "savings"' },
  { name: 'Red',      hex: '#D63B2C', usage: 'Outer arrow · alerts · cost-up warnings' },
  { name: 'Maroon',   hex: '#7A1F1A', usage: 'Arrow shadow · serious-tone accents' },
  { name: 'White',    hex: '#FFFFFF', usage: 'Cloud body · light-theme background · wordmark on dark' },
  { name: 'Grey',     hex: '#94A3B8', usage: 'Secondary text · subtle dividers · captions' },
  { name: 'Slate',    hex: '#0F172A', usage: 'Dark-theme background · navbar surfaces' },
];

const DOS = [
  'Use the white-background logo on light surfaces (decks, white-paper, light UI).',
  'Use the dark-background logo on dark hero sections, navy slides, or social previews.',
  'Maintain clear-space around the mark equal to the height of the cloud icon.',
  'Pair with our type weight: Inter / system sans for body, semibold for headings.',
];

const DONTS = [
  'Do not recolor the cloud, arrow, or wordmark — the color sequence is part of the brand.',
  'Do not stretch, skew, or rotate the logo. Always keep it square.',
  'Do not place the mark on a busy photo without a contrast scrim.',
  'Do not use any older Google-colored "C" mark — that variant is retired.',
];

function CopyHex({ hex }) {
  const [done, setDone] = React.useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(hex);
        setDone(true);
        setTimeout(() => setDone(false), 1400);
      }}
      className="inline-flex items-center gap-1 text-xs font-mono text-slate-400 hover:text-emerald-300 transition"
      data-testid={`brand-copy-${hex}`}
      aria-label={`Copy ${hex}`}
    >
      {done ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {hex}
    </button>
  );
}

export default function Brand() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <SEOHead
        title="Brand kit · CARTIEAI"
        description="Download the CARTIEAI logo (white + dark variants), color palette, and brand usage guidelines. Press, integration partners, and design teams welcome."
        ogImage={LIGHT_LOGO}
        type="website"
      />

      {/* ── Header ── */}
      <section className="border-b border-slate-900">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <Link to="/"
                className="inline-flex items-center text-xs text-slate-400 hover:text-slate-100 mb-6"
                data-testid="brand-back-home">
            <ArrowLeft className="w-3 h-3 mr-1" /> CARTIEAI · home
          </Link>
          <h1 className="text-4xl sm:text-5xl font-medium tracking-tight mb-3" data-testid="brand-h1">
            CARTIE<span className="text-emerald-300">AI</span> brand kit
          </h1>
          <p className="text-base text-slate-400 max-w-2xl">
            Two logo variants, one palette, two pages of usage rules. Press,
            integration partners, and design teams welcome — everything below
            is free to use under our brand guidelines.
          </p>
        </div>
      </section>

      {/* ── Logo download cards ── */}
      <section className="max-w-5xl mx-auto px-6 py-14">
        <h2 className="text-xs uppercase tracking-wider text-emerald-300 mb-6 inline-flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> The logo
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          {/* Light variant */}
          <Card className="bg-slate-900/40 ring-1 ring-slate-800 overflow-hidden"
                data-testid="brand-logo-light-card">
            <div className="bg-white aspect-square grid place-items-center p-10">
              <img src={LIGHT_LOGO} alt="CARTIEAI on white background"
                   className="max-w-full max-h-full"
                   data-testid="brand-logo-light-img" />
            </div>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-sm font-medium text-slate-100">White background</div>
                  <div className="text-xs text-slate-500">For light surfaces, decks, print</div>
                </div>
                <a href={LIGHT_LOGO} download="cartieai-logo-light.png"
                   className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-medium transition"
                   data-testid="brand-download-light">
                  <Download className="w-3.5 h-3.5" /> PNG
                </a>
              </div>
              <div className="text-[11px] text-slate-500 font-mono">
                /icons/cartie-logo.png · 512×512 · ~117 KB
              </div>
            </CardContent>
          </Card>

          {/* Dark variant */}
          <Card className="bg-slate-900/40 ring-1 ring-slate-800 overflow-hidden"
                data-testid="brand-logo-dark-card">
            <div className="bg-[#1c252a] aspect-square grid place-items-center p-10">
              <img src={DARK_LOGO} alt="CARTIEAI on dark background"
                   className="max-w-full max-h-full"
                   data-testid="brand-logo-dark-img" />
            </div>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-sm font-medium text-slate-100">Dark background</div>
                  <div className="text-xs text-slate-500">For dark hero, navy slides, social</div>
                </div>
                <a href={DARK_LOGO} download="cartieai-logo-dark.png"
                   className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-medium transition"
                   data-testid="brand-download-dark">
                  <Download className="w-3.5 h-3.5" /> PNG
                </a>
              </div>
              <div className="text-[11px] text-slate-500 font-mono">
                /icons/cartie-logo-on-dark.png · 512×512 · ~96 KB
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ── Color palette ── */}
      <section className="max-w-5xl mx-auto px-6 py-14 border-t border-slate-900">
        <h2 className="text-xs uppercase tracking-wider text-emerald-300 mb-6 inline-flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> Color palette
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="brand-palette">
          {PALETTE.map((c) => (
            <Card key={c.hex}
                  className="bg-slate-900/40 ring-1 ring-slate-800 overflow-hidden"
                  data-testid={`brand-color-${c.name.toLowerCase()}`}>
              <div className="h-24" style={{ backgroundColor: c.hex }} />
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-slate-100">{c.name}</div>
                  <CopyHex hex={c.hex} />
                </div>
                <div className="text-[11px] text-slate-500 leading-snug">{c.usage}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* ── Usage do's / dont's ── */}
      <section className="max-w-5xl mx-auto px-6 py-14 border-t border-slate-900">
        <h2 className="text-xs uppercase tracking-wider text-emerald-300 mb-6 inline-flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> Usage
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="bg-slate-900/40 ring-1 ring-emerald-500/30"
                data-testid="brand-dos-card">
            <CardContent className="p-6">
              <div className="text-sm font-medium text-emerald-300 mb-4">Do</div>
              <ul className="space-y-3 text-sm text-slate-300">
                {DOS.map((d, i) => (
                  <li key={i} className="flex gap-3" data-testid={`brand-do-${i + 1}`}>
                    <Check className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/40 ring-1 ring-red-500/30"
                data-testid="brand-donts-card">
            <CardContent className="p-6">
              <div className="text-sm font-medium text-red-300 mb-4">Don&apos;t</div>
              <ul className="space-y-3 text-sm text-slate-300">
                {DONTS.map((d, i) => (
                  <li key={i} className="flex gap-3" data-testid={`brand-dont-${i + 1}`}>
                    <span className="w-4 h-4 text-red-300 shrink-0 text-center leading-4">×</span>
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ── About blurb ── */}
      <section className="max-w-3xl mx-auto px-6 py-14 border-t border-slate-900">
        <h2 className="text-xs uppercase tracking-wider text-emerald-300 mb-4 inline-flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> One-line about
        </h2>
        <blockquote
          className="relative rounded-xl bg-slate-900/40 ring-1 ring-slate-800 p-6 text-base text-slate-300 leading-relaxed"
          data-testid="brand-about">
          <span className="absolute -top-3 left-4 text-3xl text-emerald-300/60 leading-none">&ldquo;</span>
          CARTIEAI is the FinOps tool engineers don&apos;t hate. It names the
          engineer, the commit, and the PR behind every cloud cost spike,
          opens an auto-PR fix in their repo, and reconciles savings against
          the actual cloud bill — so cost finally becomes accountable.
          <span className="absolute -bottom-1 right-4 text-3xl text-emerald-300/60 leading-none">&rdquo;</span>
        </blockquote>
        <p className="text-xs text-slate-500 mt-4">
          Feel free to lift the line above verbatim for press, partner pages,
          and integration directory listings.
        </p>
      </section>

      {/* ── Contact ── */}
      <section className="max-w-3xl mx-auto px-6 py-16 border-t border-slate-900 text-center">
        <h3 className="text-2xl font-medium mb-3">Need something not here?</h3>
        <p className="text-sm text-slate-400 mb-6">
          Vector files, larger PNG sizes, custom co-branded versions — drop us a line.
        </p>
        <a href="mailto:hello@cartieai.com"
           className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-medium transition"
           data-testid="brand-contact-email">
          <Mail className="w-4 h-4" /> hello@cartieai.com
        </a>
      </section>
    </div>
  );
}
